class GoogleMapsService {
  private static instance: GoogleMapsService;
  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  private placesService: google.maps.places.PlacesService | null = null;
  private geocoder: google.maps.Geocoder | null = null;

  private constructor() {}

  public static getInstance(): GoogleMapsService {
    if (!GoogleMapsService.instance) {
      GoogleMapsService.instance = new GoogleMapsService();
    }
    return GoogleMapsService.instance;
  }

  private isGoogleMapsLoaded(): boolean {
    return !!(window.google && window.google.maps && window.google.maps.places);
  }

  private getAutocompleteService(): google.maps.places.AutocompleteService {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API not loaded');
    }
    if (!this.autocompleteService) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
    }
    return this.autocompleteService;
  }

  private getPlacesService(): google.maps.places.PlacesService {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API not loaded');
    }
    if (!this.placesService) {
      this.placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
    return this.placesService;
  }

  private getGeocoder(): google.maps.Geocoder {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API not loaded');
    }
    if (!this.geocoder) {
      this.geocoder = new window.google.maps.Geocoder();
    }
    return this.geocoder;
  }

  /**
   * Get address predictions by search query (autocomplete)
   */
  public async getAddressByQuery(
    query: string,
    options?: Partial<google.maps.places.AutocompletionRequest>
  ): Promise<google.maps.places.AutocompletePrediction[]> {
    return new Promise((resolve, reject) => {
      const service = this.getAutocompleteService();
      const request: google.maps.places.AutocompletionRequest = {
        input: query,
        ...options,
      };

      service.getPlacePredictions(request, (results, status) => {
        if (results && status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Places API error: ${status}`));
        }
      });
    });
  }

  /**
   * Get place details by place ID
   */
  public async getPlaceDetails(
    placeId: string,
    fields: string[] = ['geometry', 'formatted_address', 'name']
  ): Promise<google.maps.places.PlaceResult | null> {
    return new Promise((resolve, reject) => {
      const service = this.getPlacesService();
      const request: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields,
      };

      service.getDetails(request, (place, status) => {
        if (place && status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve(null);
        } else {
          reject(new Error(`Places API error: ${status}`));
        }
      });
    });
  }

  /**
   * Get coordinates from place ID
   */
  public async getCoordinatesByPlaceId(
    placeId: string
  ): Promise<google.maps.LatLngLiteral | null> {
    const place = await this.getPlaceDetails(placeId, ['geometry']);
    if (place?.geometry?.location) {
      return {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
    }
    return null;
  }

  /**
   * Text search for places
   */
  public async textSearch(
    query: string,
    options?: Partial<google.maps.places.TextSearchRequest>
  ): Promise<google.maps.places.PlaceResult[]> {
    return new Promise((resolve, reject) => {
      const service = this.getPlacesService();
      const request: google.maps.places.TextSearchRequest = {
        query,
        ...options,
      };

      service.textSearch(request, (results, status) => {
        if (results && status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Places API error: ${status}`));
        }
      });
    });
  }

  /**
   * Geocode an address to coordinates
   */
  public async geocodeAddress(
    address: string
  ): Promise<google.maps.GeocoderResult[]> {
    return new Promise((resolve, reject) => {
      const geocoder = this.getGeocoder();

      geocoder.geocode({ address }, (results, status) => {
        if (results && status === window.google.maps.GeocoderStatus.OK) {
          resolve(results);
        } else if (status === window.google.maps.GeocoderStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Geocoder error: ${status}`));
        }
      });
    });
  }

  /**
   * Reverse geocode coordinates to address
   */
  public async reverseGeocode(
    lat: number,
    lng: number
  ): Promise<google.maps.GeocoderResult[]> {
    return new Promise((resolve, reject) => {
      const geocoder = this.getGeocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (results && status === window.google.maps.GeocoderStatus.OK) {
          resolve(results);
        } else if (status === window.google.maps.GeocoderStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Geocoder error: ${status}`));
        }
      });
    });
  }

  /**
   * Get current user location
   */
  public getCurrentLocation(): Promise<google.maps.LatLngLiteral> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          reject(new Error(`Geolocation error: ${error.message}`));
        }
      );
    });
  }
}

export const googleMapsService = GoogleMapsService.getInstance();
